import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useColumnResize } from "@/hooks/useColumnResize";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  UsersThreeIcon as UsersThree, ReceiptIcon as Receipt, GaugeIcon as Gauge,
  TerminalWindowIcon as TerminalWindow, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle,
  ProhibitIcon as Prohibit, LinkBreakIcon as LinkBreak, ArrowsClockwiseIcon as ArrowsClockwise,
  MagnifyingGlassIcon as MagnifyingGlass, WarningIcon as Warning, PackageIcon as Package, PlusIcon as Plus,
  DownloadSimpleIcon as DownloadSimple, ChartBarIcon as ChartBar,
  CaretDownIcon as CaretDown, CaretRightIcon as CaretRight,
} from "@phosphor-icons/react";

const errMsg = (e, fallback) => (e?.response?.data?.detail ? formatApiErrorDetail(e.response.data.detail) : (e?.response?.data?.message || fallback));

const CONF_LABEL = {
  very_high: { t: "Molto alto", c: "bg-green-100 text-green-800 border-green-200" },
  high: { t: "Alto", c: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  medium: { t: "Medio", c: "bg-amber-100 text-amber-800 border-amber-200" },
  low: { t: "Basso", c: "bg-orange-100 text-orange-800 border-orange-200" },
  none: { t: "Nessuno", c: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};
const STATUS_LABEL = {
  suggested: { t: "Suggerito", c: "bg-blue-100 text-blue-800" },
  confirmed: { t: "Confermato", c: "bg-green-100 text-green-800" },
  ignored: { t: "Ignorato", c: "bg-zinc-100 text-zinc-600" },
  conflict: { t: "Conflitto", c: "bg-red-100 text-red-800" },
};

const SummaryCard = ({ label, value, testid }) => (
  <div className="border border-border rounded-sm p-3" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-lg font-heading font-semibold mt-0.5">{value ?? "—"}</p>
  </div>
);

const JsonPreview = ({ data }) => (
  <pre className="mt-1 max-h-72 overflow-auto bg-zinc-900 text-zinc-100 rounded-sm p-2 text-[10px] leading-relaxed">{JSON.stringify(data, null, 2)}</pre>
);

const StatsLine = ({ s }) => s ? (
  <div className="mt-1.5" data-testid="also-subs-stats">
    <div className="flex flex-wrap gap-1.5 text-[10px]">
      <Badge variant="outline" className="rounded-sm">Letti {s.read ?? 0}</Badge>
      <Badge variant="outline" className="rounded-sm">Normalizzati {s.normalized ?? 0}</Badge>
      <Badge variant="outline" className="rounded-sm bg-green-50 text-green-700 border-green-200">Salvate nuove {s.created ?? 0}</Badge>
      <Badge variant="outline" className="rounded-sm bg-blue-50 text-blue-700 border-blue-200">Aggiornate {s.updated ?? 0}</Badge>
      <Badge variant="outline" className={`rounded-sm ${s.skipped ? "bg-amber-50 text-amber-700 border-amber-200" : ""}`}>Scartate {s.skipped ?? 0}</Badge>
    </div>
    {s.skipped_reasons?.length > 0 && (
      <ul className="mt-1 text-[10px] text-amber-700 list-disc pl-4">
        {s.skipped_reasons.map((r, i) => <li key={i}>{r}</li>)}
      </ul>
    )}
  </div>
) : null;

export function AlsoPhase2() {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null);
  const loadSummary = useCallback(() => {
    api.get("/integrations/also/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div className="mt-5 border border-border rounded-sm p-4" data-testid="also-phase2">
      <div className="flex items-center gap-2 mb-3">
        <Gauge size={16} className="text-primary" />
        <h4 className="font-heading font-semibold text-sm">Staging & Mapping ALSO</h4>
        <Badge variant="outline" className="rounded-sm text-[10px] bg-blue-50 text-blue-700 border-blue-200">Preview / staging</Badge>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="also-summary">
          <SummaryCard testid="sum-connection" label="Connessione" value={summary.connection_status === "success" ? "OK" : (summary.connection_status === "error" ? "Errore" : "—")} />
          <SummaryCard testid="sum-marketplace" label="Marketplace" value={summary.marketplace_id_primary} />
          <SummaryCard testid="sum-reseller" label="Reseller context" value={summary.reseller_context} />
          <SummaryCard testid="sum-accounts" label="Account staging" value={summary.accounts_staged} />
          <SummaryCard testid="sum-suggested" label="Mapping suggeriti" value={summary.mappings_suggested} />
          <SummaryCard testid="sum-confirmed" label="Mapping confermati" value={summary.mappings_confirmed} />
          <SummaryCard testid="sum-conflict" label="Mapping conflitto" value={summary.mappings_conflict} />
          <SummaryCard testid="sum-subs" label="Subscriptions" value={`${summary.subscriptions_staged} (M:${summary.subscriptions_microsoft} A:${summary.subscriptions_adobe} O:${summary.subscriptions_other})`} />
        </div>
      )}

      <Tabs defaultValue="clienti">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="clienti" className="rounded-sm gap-1.5" data-testid="also-tab-clienti"><UsersThree size={14} /> Clienti ALSO</TabsTrigger>
          <TabsTrigger value="sync" className="rounded-sm gap-1.5" data-testid="also-tab-sync"><ArrowsClockwise size={14} /> Sync ufficiale</TabsTrigger>
          <TabsTrigger value="licenze" className="rounded-sm gap-1.5" data-testid="also-tab-licenze"><Receipt size={14} /> Licenze</TabsTrigger>
          <TabsTrigger value="cloud" className="rounded-sm gap-1.5" data-testid="also-tab-cloud"><Receipt size={14} /> Licenze Cloud</TabsTrigger>
          <TabsTrigger value="catalogo" className="rounded-sm gap-1.5" data-testid="also-tab-catalogo"><Package size={14} /> Catalogo servizi</TabsTrigger>
          <TabsTrigger value="explorer" className="rounded-sm gap-1.5" data-testid="also-tab-explorer"><TerminalWindow size={14} /> API Explorer</TabsTrigger>
        </TabsList>
        <TabsContent value="clienti" className="mt-4"><AccountsTab busy={busy} setBusy={setBusy} onChange={loadSummary} /></TabsContent>
        <TabsContent value="sync" className="mt-4"><SyncTab /></TabsContent>
        <TabsContent value="licenze" className="mt-4"><SubscriptionsTab busy={busy} setBusy={setBusy} onChange={loadSummary} /></TabsContent>
        <TabsContent value="catalogo" className="mt-4"><ServicesTab busy={busy} setBusy={setBusy} /></TabsContent>
        <TabsContent value="cloud" className="mt-4"><LicensesTab /></TabsContent>
        <TabsContent value="explorer" className="mt-4"><ExplorerTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------------------------------------------------ Accounts tab ---
function AccountsTab({ busy, setBusy, onChange }) {
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({ q: "", associated: "", status: "" });
  const [reassign, setReassign] = useState(null);
  const [warning, setWarning] = useState(null);
  const [tableOpen, setTableOpen] = useState(true);
  const [testId, setTestId] = useState("");
  const [testRes, setTestRes] = useState(null);
  const [pasteList, setPasteList] = useState("");
  const [recalc, setRecalc] = useState(null);
  const [recalcFilter, setRecalcFilter] = useState("all");
  const [recalcOpts, setRecalcOpts] = useState({ recalc_confirmed: true, remove_unsafe: true, update_alias: true, realign_licenses: true, realign_services: true, create_if_no_match: false });

  const DW_ACC = { account: 230, piva: 110, dominio: 160, cliente: 200, match: 110, stato: 110, azioni: 320 };
  const { colW, startResize, totalW } = useColumnResize("also_accounts_table_col_w", DW_ACC);
  const RH = ({ k }) => <span onMouseDown={(e) => startResize(e, k)} className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60" />;

  const recalcPreview = async (accountId) => {
    setBusy("recalc");
    try {
      const { data } = await api.post("/integrations/also/recompute", { apply: false, account_id: accountId || undefined, options: recalcOpts });
      setRecalc({ rows: data.rows, counts: data.counts, account_id: accountId || null });
    } catch (e) { toast.error(errMsg(e, "Errore ricalcolo.")); }
    finally { setBusy(null); }
  };

  const recalcApply = async () => {
    setBusy("recalc");
    try {
      const { data } = await api.post("/integrations/also/recompute", { apply: true, account_id: recalc?.account_id || undefined, options: recalcOpts });
      const c = data.counts;
      toast.success("Ricalcolo completato", { description: `${c.corrected} corretti, ${c.unchanged} invariati, ${c.unassigned} non associati.` });
      setRecalc(null); load(); onChange();
    } catch (e) { toast.error(errMsg(e, "Errore applicazione ricalcolo.")); }
    finally { setBusy(null); }
  };

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    api.get(`/integrations/also/accounts?${p.toString()}`).then(({ data }) => setAccounts(data)).catch(() => {});
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const preview = async () => {
    setBusy("accounts"); setWarning(null);
    try {
      const { data } = await api.post("/integrations/also/accounts/preview", {});
      if (data.ok) { toast.success("Anteprima clienti ALSO", { description: data.message }); setWarning(data.warning || null); }
      else toast.error("Anteprima non riuscita", { description: data.message || "Nessun dettaglio disponibile." });
      load(); onChange();
    } catch (e) { toast.error(errMsg(e, "Impossibile leggere gli account ALSO.")); }
    finally { setBusy(null); }
  };

  const testAccount = async () => {
    if (!testId) return;
    setBusy("test-acc"); setTestRes(null);
    try {
      const { data } = await api.post("/integrations/also/accounts/test-account", { accountId: testId });
      setTestRes(data);
      if (!data.ok) toast.error("GetCompany non riuscito", { description: data.message });
    } catch (e) { toast.error(errMsg(e, "Errore GetCompany.")); }
    finally { setBusy(null); }
  };

  const addToStaging = async (aid) => {
    try {
      const { data } = await api.post("/integrations/also/accounts/add", { accountId: aid });
      if (data.ok) { toast.success(data.message); setTestRes(null); setTestId(""); load(); onChange(); }
      else toast.error("Aggiunta non riuscita", { description: data.message });
    } catch (e) { toast.error(errMsg(e, "Errore aggiunta account.")); }
  };

  const importList = async () => {
    if (!pasteList.trim()) return;
    setBusy("import");
    try {
      const { data } = await api.post("/integrations/also/accounts/import-list", { account_ids: pasteList });
      if (data.ok) { toast.success("Importazione lista", { description: data.message }); setPasteList(""); load(); onChange(); }
      else toast.error("Import non riuscito", { description: data.message });
    } catch (e) { toast.error(errMsg(e, "Errore import lista.")); }
    finally { setBusy(null); }
  };

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); onChange(); }
    catch (e) { toast.error(errMsg(e, "Operazione non riuscita.")); }
  };

  return (
    <div data-testid="also-accounts-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground">Associazione Account ALSO → Cliente TecSuite (solo match forti: alias/P.IVA/CF/tenant).</p>
        <Button size="sm" variant="outline" className="rounded-sm gap-2" disabled={busy === "recalc"} onClick={() => recalcPreview(null)} data-testid="also-recalc-btn"><ArrowsClockwise size={14} /> Ricalcola associazioni TecSuite</Button>
      </div>

      <Dialog open={!!recalc} onOpenChange={(o) => { if (!o) setRecalc(null); }}>
        <DialogContent className="rounded-sm max-w-4xl" data-testid="also-recalc-dialog">
          <DialogHeader><DialogTitle>Ricalcolo associazioni TecSuite — anteprima</DialogTitle>
            <DialogDescription>Match forti (alias/P.IVA/CF/tenant). Valori vuoti/EMPTY/onmicrosoft non generano associazioni. Nessuna cancellazione di clienti/servizi/licenze.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-3 text-[11px] mb-2">
            {[["recalc_confirmed", "Ricalcola anche confermati"], ["remove_unsafe", "Rimuovi associazioni non sicure"], ["update_alias", "Aggiorna alias"], ["realign_licenses", "Riallinea Licenze Cloud"], ["realign_services", "Riallinea Servizi"], ["create_if_no_match", "Crea nuova anagrafica se nessun match"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-1.5"><Checkbox checked={recalcOpts[k]} onCheckedChange={(v) => { setRecalcOpts((o) => ({ ...o, [k]: !!v })); }} data-testid={`recalc-opt-${k}`} /> {lbl}</label>
            ))}
            <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => recalcPreview(recalc?.account_id)}>Aggiorna anteprima</Button>
          </div>
          {recalc && (() => {
            const rows = recalc.rows.filter((r) => {
              if (recalcFilter === "changes") return r.action !== "resta invariato";
              if (recalcFilter === "euwings") return (r.current_client || "").toLowerCase().replace(/\s/g, "").includes("euwings");
              if (recalcFilter === "unassigned") return !r.recomputed_client;
              if (recalcFilter === "domain") return (r.reason || "").toLowerCase().includes("dominio");
              if (recalcFilter === "name") return (r.reason || "").toLowerCase().includes("nome");
              return true;
            });
            const FILTERS = [["all", "Tutte"], ["changes", "Solo modifiche"], ["euwings", "Solo EU Wings"], ["unassigned", "Solo non assegnati"], ["domain", "Match dominio"], ["name", "Match nome"]];
            return (
            <>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {FILTERS.map(([k, lbl]) => (
                <button key={k} onClick={() => setRecalcFilter(k)} data-testid={`recalc-filter-${k}`}
                  className={`text-[10px] px-2 py-1 rounded-sm border ${recalcFilter === k ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-muted-foreground hover:bg-secondary"}`}>{lbl}</button>
              ))}
              <span className="text-[10px] text-muted-foreground self-center ml-1">{rows.length} / {recalc.rows.length} righe</span>
            </div>
            <div className="max-h-80 overflow-auto border border-border/60 rounded-sm">
              <table className="w-full text-[11px]" data-testid="also-recalc-table">
                <thead className="text-muted-foreground border-b border-border/60 sticky top-0 bg-background"><tr className="text-left"><th className="py-1 px-2">AccountId</th><th className="py-1 px-2">Account ALSO</th><th className="py-1 px-2">Dominio</th><th className="py-1 px-2">Attuale</th><th className="py-1 px-2">Ricalcolato</th><th className="py-1 px-2">Motivo</th><th className="py-1 px-2">Azione</th></tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">Nessuna riga per questo filtro.</td></tr>}
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-border/30" data-testid="also-recalc-row">
                      <td className="py-1 px-2 font-mono">{r.account_id}</td>
                      <td className="py-1 px-2">{r.account_name || "—"}</td>
                      <td className="py-1 px-2 text-muted-foreground">{r.account_domain || "—"}</td>
                      <td className="py-1 px-2">{r.current_client || <span className="text-muted-foreground">—</span>}</td>
                      <td className="py-1 px-2">{r.recomputed_client || <span className="text-muted-foreground">—</span>}</td>
                      <td className="py-1 px-2 text-muted-foreground">{r.reason}</td>
                      <td className={`py-1 px-2 ${r.action === "sposta" || r.action === "rimuove associazione" ? "text-amber-700" : r.action === "associa" || r.action.startsWith("crea") ? "text-green-700" : ""}`}>{r.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRecalc(null)}>Annulla</Button>
            <Button className="rounded-sm" disabled={busy === "recalc"} onClick={recalcApply} data-testid="also-recalc-apply-btn">Applica ricalcolo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Strumenti manuali */}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div className="border border-border rounded-sm p-3">
          <p className="text-xs font-medium mb-2">Test / Aggiungi AccountId</p>
          <div className="flex gap-2">
            <Input value={testId} onChange={(e) => setTestId(e.target.value)} placeholder="es. 1804853" className="h-8 rounded-sm font-mono text-xs" data-testid="also-testacc-input" />
            <Button size="sm" variant="outline" className="rounded-sm h-8 text-[11px]" disabled={busy === "test-acc" || !testId} onClick={testAccount} data-testid="also-testacc-btn">Verifica</Button>
          </div>
          {testRes && (
            <div className="mt-2 text-[11px]">
              {testRes.ok
                ? (accounts.some((a) => String(a.external_account_id) === String(testId))
                    ? <div className="text-green-700 flex items-center justify-between gap-2"><span>{testRes.account?.company_name || testRes.account?.account_name || "Account valido"}</span><Badge variant="outline" className="rounded-sm text-[10px] bg-green-50 text-green-700 border-green-200" data-testid="also-testacc-already"><CheckCircle size={11} className="mr-1" /> Già in staging</Badge></div>
                    : <div className="text-green-700 flex items-center justify-between gap-2"><span>{testRes.account?.company_name || testRes.account?.account_name || "Account valido"}</span><Button size="sm" className="rounded-sm h-6 text-[10px] gap-1" onClick={() => addToStaging(testId)} data-testid="also-testacc-add-btn"><Plus size={11} /> Aggiungi a staging</Button></div>)
                : <span className="text-red-700">{testRes.message}</span>}
              {testRes.preview != null && <JsonPreview data={testRes.preview} />}
            </div>
          )}
        </div>
        <div className="border border-border rounded-sm p-3">
          <p className="text-xs font-medium mb-2">Importa lista AccountId (uno per riga)</p>
          <Textarea value={pasteList} onChange={(e) => setPasteList(e.target.value)} rows={2} placeholder={"1804853\n1234567"} className="rounded-sm font-mono text-xs" data-testid="also-import-list-input" />
          <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] mt-2" disabled={busy === "import" || !pasteList.trim()} onClick={importList} data-testid="also-import-list-btn">{busy === "import" ? "Import…" : "Importa in staging"}</Button>
        </div>
      </div>

      <div className="flex flex-nowrap items-end gap-2 mb-3 overflow-x-auto">
        <Button size="sm" className="rounded-sm gap-2 shrink-0" disabled={busy === "accounts"} onClick={preview} data-testid="also-preview-accounts-btn"><ArrowsClockwise size={14} /> {busy === "accounts" ? "Lettura…" : "Anteprima clienti/account ALSO"}</Button>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase text-muted-foreground">Cerca</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Nome, P.IVA o dominio…" className="h-8 rounded-sm text-xs pl-7" data-testid="also-filter-search" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Stato</Label>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-32" data-testid="also-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="suggested">Suggerito</SelectItem><SelectItem value="confirmed">Confermato</SelectItem><SelectItem value="ignored">Ignorato</SelectItem><SelectItem value="conflict">Conflitto</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Associato</Label>
          <Select value={filters.associated || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, associated: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-32" data-testid="also-filter-assoc"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="yes">Associati</SelectItem><SelectItem value="no">Non associati</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      {warning && (
        <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-[11px] p-2.5 mb-3 flex items-start gap-2" data-testid="also-accounts-warning">
          <Warning size={14} className="mt-0.5 shrink-0" /><span>{warning}</span>
        </div>
      )}

      {accounts.length === 0
        ? <p className="text-xs text-muted-foreground py-6 text-center">Nessun account in staging. Usa "Anteprima clienti/account ALSO" o aggiungi manualmente un AccountId.</p>
        : (
          <div className="border border-border/60 rounded-sm" data-testid="also-accounts-collapsible">
            <button type="button" onClick={() => setTableOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium hover:bg-secondary/50 transition-colors" data-testid="also-accounts-toggle">
              <span className="flex items-center gap-2">{tableOpen ? <CaretDown size={14} /> : <CaretRight size={14} />} Elenco clienti/account ALSO <Badge variant="outline" className="rounded-sm text-[10px]">{accounts.length}</Badge></span>
              <span className="text-muted-foreground">{tableOpen ? "Chiudi" : "Apri"}</span>
            </button>
            {tableOpen && (
            <div className="overflow-x-auto border-t border-border/60">
            <table className="text-xs [&_td]:overflow-hidden [&_th]:overflow-hidden" style={{ tableLayout: "fixed", width: totalW }} data-testid="also-accounts-table">
              <thead className="text-muted-foreground border-b border-border/60"><tr className="text-left">
                <th className="py-1 pr-3 relative" style={{ width: colW.account }}>Account ALSO<RH k="account" /></th>
                <th className="py-1 pr-3 relative" style={{ width: colW.piva }}>P.IVA<RH k="piva" /></th>
                <th className="py-1 pr-3 relative" style={{ width: colW.dominio }}>Dominio<RH k="dominio" /></th>
                <th className="py-1 pr-3 relative" style={{ width: colW.cliente }}>Cliente TecSuite<RH k="cliente" /></th>
                <th className="py-1 pr-3 relative" style={{ width: colW.match }}>Match<RH k="match" /></th>
                <th className="py-1 pr-3 relative" style={{ width: colW.stato }}>Stato<RH k="stato" /></th>
                <th className="py-1 relative" style={{ width: colW.azioni }}>Azioni<RH k="azioni" /></th>
              </tr></thead>
              <tbody>
                {accounts.map((a) => {
                  const m = a.mapping || {};
                  const conf = CONF_LABEL[m.match_confidence] || CONF_LABEL.none;
                  const st = STATUS_LABEL[m.status] || STATUS_LABEL.suggested;
                  return (
                    <tr key={a.id} className="border-b border-border/40 align-top" data-testid="also-account-row">
                      <td className="py-1.5 pr-3"><div className="font-medium">{a.account_name || a.company_name || "—"}</div><div className="font-mono text-[10px] text-muted-foreground">{a.external_account_id}{a.source_method ? ` · ${a.source_method}` : ""}</div></td>
                      <td className="py-1.5 pr-3">{a.vat_id || "—"}</td>
                      <td className="py-1.5 pr-3">{a.domain || "—"}</td>
                      <td className="py-1.5 pr-3">{m.tecsuite_client_name || <span className="text-muted-foreground">non associato</span>}</td>
                      <td className="py-1.5 pr-3"><Badge variant="outline" className={`rounded-sm text-[10px] ${conf.c}`} title={m.match_reason}>{conf.t}</Badge></td>
                      <td className="py-1.5 pr-3"><Badge variant="outline" className={`rounded-sm text-[10px] ${st.c}`}>{st.t}</Badge></td>
                      <td className="py-1.5">
                        <div className="flex flex-wrap gap-1">
                          {m.tecsuite_client_id && m.status !== "confirmed" && <Button size="sm" variant="outline" className="h-6 rounded-sm text-[10px] px-2 gap-1" data-testid="also-confirm-btn" onClick={() => act(() => api.post(`/integrations/also/mappings/${m.id}/confirm`, {}), "Mapping confermato")}><CheckCircle size={11} /> Conferma</Button>}
                          {m.id && <Button size="sm" variant="outline" className="h-6 rounded-sm text-[10px] px-2 gap-1" data-testid="also-reassign-btn" onClick={() => setReassign(m)}><MagnifyingGlass size={11} /> Cambia</Button>}
                          {m.tecsuite_client_id && <Button size="sm" variant="outline" className="h-6 rounded-sm text-[10px] px-2 gap-1" data-testid="also-unlink-btn" onClick={() => act(() => api.delete(`/integrations/also/mappings/${m.id}/association`), "Associazione rimossa")}><LinkBreak size={11} /> Rimuovi</Button>}
                          {m.id && m.status !== "ignored" && <Button size="sm" variant="outline" className="h-6 rounded-sm text-[10px] px-2 gap-1" data-testid="also-ignore-btn" onClick={() => act(() => api.post(`/integrations/also/mappings/${m.id}/ignore`), "Account ignorato")}><Prohibit size={11} /> Ignora</Button>}
                          {m.external_account_id && <Button size="sm" variant="outline" className="h-6 rounded-sm text-[10px] px-2 gap-1" data-testid="also-recalc-row-btn" onClick={() => recalcPreview(m.external_account_id)}><ArrowsClockwise size={11} /> Ricalcola</Button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            )}
          </div>
        )}

      <ReassignDialog mapping={reassign} onClose={() => setReassign(null)} onDone={() => { setReassign(null); load(); onChange(); }} />
    </div>
  );
}

function ReassignDialog({ mapping, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => { api.get(`/integrations/also/clients/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)).catch(() => {}); }, 300);
    return () => clearTimeout(t);
  }, [q]);
  const pick = async (clientId) => {
    try { await api.post(`/integrations/also/mappings/${mapping.id}/reassign`, { tecsuite_client_id: clientId }); toast.success("Cliente associato"); onDone(); }
    catch (e) { toast.error(errMsg(e, "Errore associazione.")); }
  };
  return (
    <Dialog open={!!mapping} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="also-reassign-dialog">
        <DialogHeader><DialogTitle>Associa cliente TecSuite</DialogTitle><DialogDescription>Account ALSO: {mapping?.external_account_name || mapping?.external_account_id}. Salva solo il mapping, non modifica l'anagrafica.</DialogDescription></DialogHeader>
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ragione sociale, P.IVA o email…" className="rounded-sm" data-testid="also-client-search-input" />
        <div className="max-h-64 overflow-y-auto mt-2 space-y-1">
          {results.map((c) => (
            <button key={c.id} onClick={() => pick(c.id)} className="w-full text-left border border-border/60 rounded-sm p-2 hover:bg-secondary transition-colors" data-testid="also-client-result">
              <div className="text-sm font-medium">{c.ragione_sociale}</div>
              <div className="text-[11px] text-muted-foreground">{c.partita_iva || "—"} · {c.citta || ""} · {c.email || ""}</div>
            </button>
          ))}
          {q.length >= 2 && results.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">Nessun cliente trovato.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------- Subscriptions tab ---
function SubscriptionsTab({ busy, setBusy, onChange }) {
  const [subs, setSubs] = useState([]);
  const [filters, setFilters] = useState({ vendor: "", status: "", associated: "", product: "" });
  const [testId, setTestId] = useState("");
  const [reseller, setReseller] = useState("1727168");
  const [testRes, setTestRes] = useState(null);
  const [confirmBatch, setConfirmBatch] = useState(false);
  const [accountsCount, setAccountsCount] = useState(0);
  const [batchRes, setBatchRes] = useState(null);
  const [showTech, setShowTech] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    api.get(`/integrations/also/subscriptions?${p.toString()}`).then(({ data }) => setSubs(data)).catch(() => {});
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/integrations/also/accounts").then(({ data }) => setAccountsCount(data.length)).catch(() => {}); }, []);

  const testSubs = async () => {
    if (!testId) return;
    setBusy("subs-test"); setTestRes(null);
    try {
      const { data } = await api.post("/integrations/also/subscriptions/test-account", { accountId: testId, resellerContext: reseller, save: false });
      setTestRes(data);
      if (data.ok) toast.success("Test subscriptions", { description: `${data.count ?? 0} record letti da ALSO per l'account ${testId}` });
      else toast.error("Test non riuscito", { description: data.message });
    } catch (e) { toast.error(errMsg(e, "Errore test subscriptions.")); }
    finally { setBusy(null); }
  };

  const saveSubs = async () => {
    if (!testId) return;
    setBusy("subs-save");
    try {
      const { data } = await api.post("/integrations/also/subscriptions/test-account", { accountId: testId, resellerContext: reseller, save: true });
      setTestRes(data);
      if (data.ok) toast.success("Subscriptions salvate in staging", { description: data.message });
      else toast.error("Salvataggio non riuscito", { description: data.message });
      load(); onChange();
    } catch (e) { toast.error(errMsg(e, "Errore salvataggio subscriptions.")); }
    finally { setBusy(null); }
  };

  const runBatch = async () => {
    setConfirmBatch(false); setBusy("subs-batch"); setBatchRes(null);
    try {
      const { data } = await api.post("/integrations/also/subscriptions/preview-batch", {});
      setBatchRes(data);
      if (data.ok) toast.success("Batch subscriptions", { description: data.message });
      else toast.error("Batch non riuscito", { description: data.message });
      load(); onChange();
    } catch (e) { toast.error(errMsg(e, "Errore batch subscriptions.")); }
    finally { setBusy(null); }
  };

  const visibleSubs = showTech ? subs : subs.filter((s) => !["tenant_service", "technical_service"].includes(s.record_type));

  return (
    <div data-testid="also-subscriptions-tab">
      <div className="border border-border rounded-sm p-3 mb-3">
        <p className="text-xs font-medium mb-2">Test subscriptions per AccountId (read-only), poi salvataggio esplicito in staging</p>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-[10px] uppercase text-muted-foreground">AccountId</Label><Input value={testId} onChange={(e) => setTestId(e.target.value)} placeholder="es. 1807569 (4M Serramenti)" className="h-8 rounded-sm font-mono text-xs w-64" data-testid="also-subtest-account" /></div>
          <div><Label className="text-[10px] uppercase text-muted-foreground">ResellerContext</Label><Input value={reseller} onChange={(e) => setReseller(e.target.value)} className="h-8 rounded-sm font-mono text-xs w-32" data-testid="also-subtest-reseller" /></div>
          <Button size="sm" variant="outline" className="rounded-sm h-8 text-[11px]" disabled={busy === "subs-test" || busy === "subs-save" || !testId} onClick={testSubs} data-testid="also-subtest-btn">{busy === "subs-test" ? "Test…" : "Test GetSubscriptions"}</Button>
          <Button size="sm" className="rounded-sm h-8 text-[11px] gap-1" disabled={busy === "subs-save" || busy === "subs-test" || !testId || !(testRes && testRes.ok)} onClick={saveSubs} data-testid="also-subsave-btn"><Plus size={12} /> {busy === "subs-save" ? "Salvataggio…" : "Salva queste subscription in staging"}</Button>
        </div>
        {testRes && (
          <div className="mt-2 text-[11px]">
            <div className="flex items-center gap-2">{testRes.ok ? <span className="text-green-700 flex items-center gap-1"><CheckCircle size={12} weight="fill" /> OK {testRes.count != null ? `· ${testRes.count} record letti` : ""}</span> : <span className="text-red-700">{testRes.message}</span>}</div>
            {testRes.stats && <StatsLine s={testRes.stats} />}
            {testRes.stats == null && testRes.ok && <p className="text-[10px] text-muted-foreground mt-1">Anteprima read-only. Premi "Salva queste subscription in staging" per scriverle.</p>}
            {testRes.preview != null && <JsonPreview data={testRes.preview} />}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <Button size="sm" variant="outline" className="rounded-sm gap-2" disabled={busy} onClick={() => setConfirmBatch(true)} data-testid="also-preview-subs-batch-btn"><ArrowsClockwise size={14} /> {busy === "subs-batch" ? "Batch…" : "Anteprima massiva (max 25)"}</Button>
        <label className="flex items-center gap-1.5 text-[11px]"><Checkbox checked={showTech} onCheckedChange={(v) => setShowTech(!!v)} data-testid="also-subs-showtech" /> Mostra tenant/tecnici</label>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Vendor</Label>
          <Select value={filters.vendor || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, vendor: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-32" data-testid="also-filter-vendor"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="microsoft">Microsoft</SelectItem><SelectItem value="adobe">Adobe</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Prodotto</Label><Input value={filters.product} onChange={(e) => setFilters((f) => ({ ...f, product: e.target.value }))} className="h-8 rounded-sm text-xs" data-testid="also-filter-product" /></div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Associato</Label>
          <Select value={filters.associated || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, associated: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-32" data-testid="also-filter-subs-assoc"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="yes">Associati</SelectItem><SelectItem value="no">Non associati</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      {batchRes && (
        <div className="border border-border rounded-sm p-3 mb-3 bg-zinc-50/50" data-testid="also-batch-result">
          <p className="text-xs font-medium mb-1">Esito anteprima massiva</p>
          <StatsLine s={batchRes.stats} />
          {batchRes.stopped_for_rate_limit && <p className="text-[10px] text-amber-700 mt-1">Interrotto per rate limit ALSO.</p>}
          <div className="mt-2 max-h-40 overflow-auto">
            <table className="w-full text-[10px]"><tbody>
              {(batchRes.results || []).map((r) => (
                <tr key={r.account_id} className="border-b border-border/30">
                  <td className="py-0.5 pr-2 font-mono">{r.account_id}</td>
                  <td className="py-0.5 pr-2">{r.ok ? <span className="text-green-700">OK</span> : <span className="text-red-700">Errore</span>}</td>
                  <td className="py-0.5 pr-2">letti {r.read ?? r.count ?? 0}</td>
                  <td className="py-0.5 pr-2">nuove {r.created ?? 0}</td>
                  <td className="py-0.5">agg. {r.updated ?? 0}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </div>
      )}

      {visibleSubs.length === 0
        ? <p className="text-xs text-muted-foreground py-6 text-center">Nessuna subscription in staging{!showTech && subs.length > 0 ? " (tenant/tecnici nascosti: attiva \"Mostra tenant/tecnici\")" : ""}. Prima testa un singolo AccountId, poi salva in staging o esegui il batch.</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="also-subs-table">
              <thead className="text-muted-foreground border-b border-border/60"><tr className="text-left"><th className="py-1 pr-3">Cliente ALSO</th><th className="py-1 pr-3">Cliente TecSuite</th><th className="py-1 pr-3">Vendor</th><th className="py-1 pr-3">Prodotto</th><th className="py-1 pr-3">Tipo</th><th className="py-1 pr-3">SKU</th><th className="py-1 pr-3">Q.tà</th><th className="py-1 pr-3">Stato</th><th className="py-1 pr-3">Rinnovo</th><th className="py-1">Prezzo</th></tr></thead>
              <tbody>
                {visibleSubs.map((s) => (
                  <tr key={s.id} className="border-b border-border/40" data-testid="also-sub-row">
                    <td className="py-1.5 pr-3">{s.external_account_name || s.external_account_id || "—"}</td>
                    <td className="py-1.5 pr-3">{s.tecsuite_client_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-1.5 pr-3"><Badge variant="outline" className={`rounded-sm text-[10px] ${s.vendor === "microsoft" ? "bg-blue-100 text-blue-800" : s.vendor === "adobe" ? "bg-red-100 text-red-800" : "bg-zinc-100 text-zinc-600"}`}>{s.vendor}</Badge></td>
                    <td className="py-1.5 pr-3">{s.product_name || s.display_name || "—"}</td>
                    <td className="py-1.5 pr-3 text-[10px]">{s.record_type === "license_subscription" ? "Licenza" : s.record_type === "tenant_service" ? "Tenant" : s.record_type === "technical_service" ? "Tecnico" : "Altro"}</td>
                    <td className="py-1.5 pr-3 font-mono text-[10px]">{s.sku || s.service_name || "—"}</td>
                    <td className="py-1.5 pr-3">{s.quantity ?? "—"}</td>
                    <td className="py-1.5 pr-3">{s.status || "—"}</td>
                    <td className="py-1.5 pr-3">{s.renewal_date || s.end_date || "—"}</td>
                    <td className="py-1.5">{s.price != null ? `${s.price} ${s.currency || ""}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={confirmBatch} onOpenChange={setConfirmBatch}>
        <DialogContent className="rounded-sm" data-testid="also-batch-confirm">
          <DialogHeader><DialogTitle>Conferma anteprima massiva</DialogTitle>
            <DialogDescription>
              Verranno interrogati fino a {Math.min(accountsCount, 25)} account in staging (max 25 per batch). {accountsCount < 2 ? "Attenzione: hai meno di 2 account in staging, il batch potrebbe non essere significativo. " : ""}Operazione read-only: si ferma in caso di rate limit.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmBatch(false)}>Annulla</Button>
            <Button className="rounded-sm" onClick={runBatch} data-testid="also-batch-confirm-btn">Esegui batch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------------------------------------------------- Services tab ---
function ServicesTab({ busy, setBusy }) {
  const [data, setData] = useState({ total: 0, shown: 0, services: [] });
  const [filters, setFilters] = useState({ q: "", vendor: "" });

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.q) p.append("q", filters.q);
    if (filters.vendor) p.append("vendor", filters.vendor);
    api.get(`/integrations/also/services?${p.toString()}`).then(({ data }) => setData(data)).catch(() => {});
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const cache = async () => {
    setBusy("services");
    try {
      const { data } = await api.post("/integrations/also/services/cache", {});
      if (data.ok) toast.success("Catalogo servizi", { description: data.message });
      else toast.error("Cache non riuscita", { description: data.message });
      load();
    } catch (e) { toast.error(errMsg(e, "Errore caricamento catalogo servizi.")); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="also-services-tab">
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <Button size="sm" className="rounded-sm gap-2" disabled={busy === "services"} onClick={cache} data-testid="also-cache-services-btn"><ArrowsClockwise size={14} /> {busy === "services" ? "Lettura…" : "Aggiorna catalogo (ListMarketplaceServices)"}</Button>
        <div><Label className="text-[10px] uppercase text-muted-foreground">Cerca</Label><Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Office, Azure, Acrobat…" className="h-8 rounded-sm text-xs w-48" data-testid="also-svc-search" /></div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Vendor</Label>
          <Select value={filters.vendor || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, vendor: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-32" data-testid="also-svc-vendor"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="microsoft">Microsoft</SelectItem><SelectItem value="adobe">Adobe</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
          </Select>
        </div>
        <span className="text-[11px] text-muted-foreground">Totale in cache: {data.total} · mostrati: {data.shown}</span>
      </div>

      {data.services.length === 0
        ? <p className="text-xs text-muted-foreground py-6 text-center">Catalogo servizi vuoto. Premi "Aggiorna catalogo" per leggerlo (read-only, solo cache).</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="also-services-table">
              <thead className="text-muted-foreground border-b border-border/60"><tr className="text-left"><th className="py-1 pr-3">Prodotto / Servizio</th><th className="py-1 pr-3">Display</th><th className="py-1 pr-3">Owner/Vendor</th><th className="py-1 pr-3">Vendor</th><th className="py-1">ServiceId</th></tr></thead>
              <tbody>
                {data.services.map((s) => (
                  <tr key={s.id} className="border-b border-border/40" data-testid="also-service-row">
                    <td className="py-1.5 pr-3">{s.product_name || s.service_name || "—"}</td>
                    <td className="py-1.5 pr-3">{s.display_name || "—"}</td>
                    <td className="py-1.5 pr-3">{s.owner_name || "—"}</td>
                    <td className="py-1.5 pr-3"><Badge variant="outline" className={`rounded-sm text-[10px] ${s.vendor === "microsoft" ? "bg-blue-100 text-blue-800" : s.vendor === "adobe" ? "bg-red-100 text-red-800" : "bg-zinc-100 text-zinc-600"}`}>{s.vendor}</Badge></td>
                    <td className="py-1.5 font-mono text-[10px]">{s.service_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

// ------------------------------------------------------------- Explorer tab --
const PRESETS = [
  { label: "GetCompany reseller (1727168)", endpoint: "GetCompany", body: { accountId: 1727168 } },
  { label: "GetCompany EuWings (1804853)", endpoint: "GetCompany", body: { accountId: 1804853 } },
  { label: "ListMarketplaceServices (99584)", endpoint: "ListMarketplaceServices", body: { marketplaceId: 99584 } },
  { label: "GetSubscriptions reseller", endpoint: "GetSubscriptions", body: { parentAccountId: 1727168, resellerContext: 1727168 } },
  { label: "GetSubscriptions EuWings", endpoint: "GetSubscriptions", body: { parentAccountId: 1804853, resellerContext: 1727168 } },
];

function ExplorerTab() {
  const [allowlist, setAllowlist] = useState([]);
  const [endpoint, setEndpoint] = useState("");
  const [body, setBody] = useState("{}");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => { api.get("/integrations/also/explorer/allowlist").then(({ data }) => setAllowlist(data)).catch(() => {}); }, []);

  const applyPreset = (label) => {
    const p = PRESETS.find((x) => x.label === label);
    if (p) { setEndpoint(p.endpoint); setBody(JSON.stringify(p.body, null, 2)); }
  };

  const run = async () => {
    let parsed = {};
    try { parsed = body.trim() ? JSON.parse(body) : {}; } catch { toast.error("Body JSON non valido"); return; }
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/integrations/also/explorer", { endpoint, body: parsed });
      setResult(data);
      if (!data.ok) toast.error("Chiamata non riuscita", { description: data.message });
    } catch (e) { toast.error(errMsg(e, "Errore explorer.")); }
    finally { setRunning(false); }
  };

  return (
    <div data-testid="also-explorer-tab">
      <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-[11px] p-2.5 mb-3">Read-only. Solo endpoint in allowlist; ogni metodo di scrittura è bloccato. Attenzione alla case dei parametri (accountId, parentAccountId, resellerContext, marketplaceId).</div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="min-w-[220px]">
          <Label className="text-[10px] uppercase text-muted-foreground">Preset</Label>
          <Select onValueChange={applyPreset}>
            <SelectTrigger className="h-9 rounded-sm text-xs" data-testid="also-explorer-preset"><SelectValue placeholder="Scegli preset" /></SelectTrigger>
            <SelectContent>{PRESETS.map((p) => <SelectItem key={p.label} value={p.label}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <Label className="text-[10px] uppercase text-muted-foreground">Endpoint (allowlist)</Label>
          <Select value={endpoint} onValueChange={setEndpoint}>
            <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="also-explorer-endpoint"><SelectValue placeholder="Endpoint" /></SelectTrigger>
            <SelectContent>{allowlist.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button className="rounded-sm gap-2" disabled={!endpoint || running} onClick={run} data-testid="also-explorer-run-btn"><TerminalWindow size={14} /> {running ? "Esecuzione…" : "Esegui"}</Button>
      </div>
      <Label className="text-[10px] uppercase text-muted-foreground">Body JSON</Label>
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} className="mt-1 rounded-sm font-mono text-xs" data-testid="also-explorer-body" />
      {result && (
        <div className="mt-3 border border-border/60 rounded-sm p-3 bg-zinc-50/50" data-testid="also-explorer-result">
          <div className="flex items-center gap-2 text-xs mb-1">{result.ok ? <span className="text-green-700 flex items-center gap-1"><CheckCircle size={13} weight="fill" /> OK</span> : <span className="text-red-700 flex items-center gap-1"><XCircle size={13} weight="fill" /> Errore</span>}{result.http_status != null && <span className="text-muted-foreground">HTTP {result.http_status}</span>}{result.count != null && <span className="text-muted-foreground">· {result.count} record</span>}</div>
          {result.message && <p className="text-[11px] text-muted-foreground mb-1">{result.message}</p>}
          {result.preview != null && <JsonPreview data={result.preview} />}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------- Sync ufficiale ---
function SyncTab() {
  const [sched, setSched] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [last, setLast] = useState(null);

  const load = useCallback(() => {
    api.get("/integrations/also/scheduler").then(({ data }) => setSched(data)).catch(() => {});
    api.get("/integrations/also/sync/logs").then(({ data }) => { setLogs(data); if (data[0]) setLast(data[0]); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    setConfirm(false); setBusy(true);
    try {
      const { data } = await api.post("/integrations/also/sync/run");
      setLast(data);
      if (["success", "partial"].includes(data.status)) toast.success("Sync ALSO completata", { description: data.message });
      else toast.error("Sync ALSO non riuscita", { description: (data.errors && data.errors[0]) || data.message });
      load();
    } catch (e) { toast.error(errMsg(e, "Errore sync.")); }
    finally { setBusy(false); }
  };

  const runStaging = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/also/sync/run-staging-snapshot");
      setLast(data);
      toast.warning("Import da snapshot staging (debug)", { description: data.message });
      load();
    } catch (e) { toast.error(errMsg(e, "Errore import staging.")); }
    finally { setBusy(false); }
  };

  const repairAssoc = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/also/repair/preview");
      const msg = `Clienti con più AccountId ALSO: ${data.clients_with_multiple_also_ids} (${data.also_ids_involved} AccountId). Procedere con la riparazione? Verranno corretti solo i collegamenti errati source=also (nessuna cancellazione).`;
      if (!window.confirm(msg)) { setBusy(false); return; }
      const { data: r } = await api.post("/integrations/also/repair/apply");
      toast.success("Associazioni ALSO riparate", { description: `Alias rimossi ${r.aliases_removed}, riassociati clienti ${r.clients_associated}, servizi ${r.services_reassigned}, licenze ${r.licenses_reassigned}.` });
      load();
    } catch (e) { toast.error(errMsg(e, "Errore riparazione.")); }
    finally { setBusy(false); }
  };

  const saveSched = async (patch) => {
    try {
      const { data } = await api.put("/integrations/also/scheduler", { ...sched, ...patch });
      setSched(data);
      toast.success("Scheduler aggiornato");
    } catch (e) { toast.error(errMsg(e, "Errore scheduler.")); }
  };

  const num = (v) => v ?? 0;

  return (
    <div data-testid="also-sync-tab">
      <div className="rounded-sm bg-blue-50 border border-blue-200 text-blue-800 text-[11px] p-2.5 mb-3">Importa/aggiorna anagrafiche clienti e servizi/licenze da ALSO (dallo staging). Non crea ordini e non scrive nulla su ALSO. Match automatico solo al 100% (P.IVA, CF, alias, tenant); altrimenti crea una nuova anagrafica da unire manualmente.</div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button size="sm" className="rounded-sm gap-2" disabled={busy} onClick={() => setConfirm(true)} data-testid="also-sync-run-btn"><ArrowsClockwise size={14} /> {busy ? "Sincronizzazione…" : "Sincronizza ora"}</Button>
        <Button size="sm" variant="outline" className="rounded-sm gap-2" disabled={busy} onClick={repairAssoc} data-testid="also-repair-btn"><Warning size={14} /> Ripara associazioni</Button>
        {last && <span className="text-[11px] text-muted-foreground">Ultima: {last.finished_at ? String(last.finished_at).slice(0, 19).replace("T", " ") : "—"} · <b className={last.status === "success" ? "text-green-700" : last.status === "partial" ? "text-amber-700" : "text-red-700"}>{last.status}</b> · <Badge variant="outline" className={`rounded-sm text-[9px] ${last.live ? "bg-green-50 text-green-700 border-green-200" : "bg-zinc-100 text-zinc-600"}`}>{last.live ? "LIVE" : (last.used_staging_snapshot ? "STAGING (debug)" : "no dati")}</Badge></span>}
      </div>

      {last && ["failed", "critical_error"].includes(last.status) && (
        <div className="rounded-sm bg-red-50 border border-red-200 text-red-800 text-[11px] p-2.5 mb-4 flex items-start gap-2" data-testid="also-sync-error">
          <Warning size={14} className="mt-0.5 shrink-0" />
          <div>
            <b>Sync non riuscita ({last.critical || last.status}).</b> Nessun dato è stato scritto su TecSuite: le anagrafiche, le Licenze Cloud e i Servizi restano invariati.
            <div className="mt-1">{(last.errors && last.errors[0]) || last.message}</div>
            <div className="mt-1.5"><Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" disabled={busy} onClick={runStaging} data-testid="also-sync-staging-btn">Usa ultimo staging snapshot (solo debug)</Button></div>
          </div>
        </div>
      )}

      {last && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4" data-testid="also-sync-summary">
          <KpiCard testid="sync-read" label="Clienti letti" value={num(last.clients_read)} />
          <KpiCard testid="sync-created" label="Nuovi clienti" value={num(last.clients_created)} tone={last.clients_created ? "border-emerald-300 bg-emerald-50" : ""} />
          <KpiCard testid="sync-assoc" label="Associati auto" value={num(last.clients_associated)} />
          <KpiCard testid="sync-svc-new" label="Servizi creati" value={num(last.services_created)} />
          <KpiCard testid="sync-svc-upd" label="Servizi aggiornati" value={num(last.services_updated)} />
          <KpiCard testid="sync-unassigned" label="Non assegnati" value={num(last.services_unassigned)} tone={last.services_unassigned ? "border-amber-300 bg-amber-50" : ""} />
        </div>
      )}
      {last && (
        <div className="flex flex-wrap gap-2 mb-4 text-[11px]">
          <a href="/clienti" className="text-primary hover:underline">Vai ad Anagrafiche</a>
          <span className="text-muted-foreground">·</span>
          <a href="/servizi" className="text-primary hover:underline">Vai a Servizi (non assegnati)</a>
          {last.errors_count > 0 && <><span className="text-muted-foreground">·</span><span className="text-red-700">{last.errors_count} errori</span></>}
        </div>
      )}

      {last && last.services_unassigned > 0 && (
        <div className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 text-[11px] p-2.5 mb-4 flex items-center gap-2" data-testid="also-sync-unassigned-alert">
          <Warning size={14} className="shrink-0" />
          <span>Ci sono <b>{last.services_unassigned}</b> servizi/licenze ALSO non assegnati. </span>
          <a href="/servizi?unassigned=1" className="text-primary hover:underline font-medium">Vai al filtro "Solo non assegnati"</a>
        </div>
      )}

      {sched && (
        <div className="border border-border rounded-sm p-3 mb-4" data-testid="also-scheduler">
          <div className="flex items-center gap-2 mb-2"><Gauge size={15} className="text-primary" /><span className="text-xs font-semibold">Sincronizzazione automatica</span></div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex items-center gap-1.5 text-[11px]"><Checkbox checked={sched.enabled} onCheckedChange={(v) => saveSched({ enabled: !!v })} data-testid="also-sched-enabled" /> Abilita scheduler</label>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Frequenza</Label>
              <Select value={sched.frequency} onValueChange={(v) => saveSched({ frequency: v })}>
                <SelectTrigger className="h-8 rounded-sm text-xs w-28" data-testid="also-sched-freq"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="daily">Giornaliera</SelectItem><SelectItem value="weekly">Settimanale</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase text-muted-foreground">Ora (UTC)</Label><Input type="number" min={0} max={23} value={sched.hour} onChange={(e) => setSched((s) => ({ ...s, hour: Number(e.target.value) }))} onBlur={() => saveSched({ hour: sched.hour })} className="h-8 rounded-sm text-xs w-20" data-testid="also-sched-hour" /></div>
            <label className="flex items-center gap-1.5 text-[11px]"><Checkbox checked={sched.sync_clients} onCheckedChange={(v) => saveSched({ sync_clients: !!v })} /> Clienti</label>
            <label className="flex items-center gap-1.5 text-[11px]"><Checkbox checked={sched.import_services} onCheckedChange={(v) => saveSched({ import_services: !!v })} /> Servizi</label>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Default: disattivato. Orario in fuso <b>Europe/Rome</b>. Prossima esecuzione: <b>{sched.next_run_local || "—"}</b> (Europe/Rome){sched.next_run_at ? ` · ${String(sched.next_run_at).slice(11, 16)} UTC` : ""}. Ultimo esito: {sched.last_status || "—"}{sched.last_error ? ` · ${sched.last_error}` : ""}.</p>
        </div>
      )}

      <p className="text-xs font-medium mb-1">Log sincronizzazioni</p>
      {logs.length === 0
        ? <p className="text-xs text-muted-foreground py-3">Nessuna sincronizzazione eseguita.</p>
        : (
          <div className="overflow-x-auto border border-border/60 rounded-sm">
            <table className="w-full text-[11px]" data-testid="also-sync-logs">
              <thead className="text-muted-foreground border-b border-border/60"><tr className="text-left"><th className="py-1 px-2">Data</th><th className="py-1 px-2">Trigger</th><th className="py-1 px-2">Esito</th><th className="py-1 px-2">Letti</th><th className="py-1 px-2">Creati</th><th className="py-1 px-2">Assoc.</th><th className="py-1 px-2">Svc +/~</th><th className="py-1 px-2">Non ass.</th><th className="py-1 px-2">Err.</th></tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-border/30">
                    <td className="py-1 px-2">{l.finished_at ? String(l.finished_at).slice(0, 19).replace("T", " ") : "—"}</td>
                    <td className="py-1 px-2">{l.trigger}</td>
                    <td className={`py-1 px-2 ${l.status === "success" ? "text-green-700" : l.status === "error" ? "text-red-700" : "text-amber-700"}`}>{l.status}</td>
                    <td className="py-1 px-2">{num(l.clients_read)}</td>
                    <td className="py-1 px-2">{num(l.clients_created)}</td>
                    <td className="py-1 px-2">{num(l.clients_associated)}</td>
                    <td className="py-1 px-2">{num(l.services_created)}/{num(l.services_updated)}</td>
                    <td className="py-1 px-2">{num(l.services_unassigned)}</td>
                    <td className="py-1 px-2">{num(l.errors_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="rounded-sm" data-testid="also-sync-confirm">
          <DialogHeader><DialogTitle>Conferma sincronizzazione ALSO</DialogTitle>
            <DialogDescription>Questa operazione importerà/aggiornerà anagrafiche clienti e servizi/licenze da ALSO. Non verranno creati ordini e non verrà scritto nulla su ALSO.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirm(false)}>Annulla</Button>
            <Button className="rounded-sm" onClick={runSync} data-testid="also-sync-confirm-btn">Sincronizza ora</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------- Licenze Cloud ---
const daysUntil = (v) => {
  if (!v) return null;
  const m = String(v).match(/\/Date\((\d+)/);
  const d = m ? new Date(parseInt(m[1], 10)) : new Date(v);
  if (isNaN(d.getTime())) return null;
  return Math.floor((d - new Date()) / 86400000);
};
const eur = (v) => (v == null ? "n/d" : `${Number(v).toFixed(2)} €`);
const pct = (v) => (v == null ? "n/d" : `${Number(v).toFixed(1)}%`);

const LicBadges = ({ s }) => {
  const b = [];
  const cd = daysUntil(s.contract_end_date || s.end_date || s.commitment_end_date);
  if (cd !== null && cd >= 0 && cd <= 90) b.push({ t: `Scade ${cd}g`, c: cd <= 30 ? "bg-red-100 text-red-800" : cd <= 60 ? "bg-orange-100 text-orange-800" : "bg-amber-100 text-amber-800" });
  const pd = daysUntil(s.price_protection_end_date);
  if (pd !== null && pd >= 0 && pd <= 90) b.push({ t: `PP ${pd}g`, c: "bg-purple-100 text-purple-800" });
  const act = (s.advance_period_end_action || "").toLowerCase();
  if (act.includes("terminate")) b.push({ t: "Termina fine periodo", c: "bg-red-100 text-red-800" });
  else if (act.includes("renew")) b.push({ t: "Rinnovo autom.", c: "bg-emerald-100 text-emerald-800" });
  if (!s.tecsuite_client_id) b.push({ t: "Mapping mancante", c: "bg-zinc-200 text-zinc-700" });
  if (s.sales_price == null || s.purchase_price == null) b.push({ t: "Prezzo mancante", c: "bg-yellow-100 text-yellow-800" });
  if (!s.quantity) b.push({ t: "Q.tà mancante", c: "bg-yellow-100 text-yellow-800" });
  return b.length ? <div className="flex flex-wrap gap-1">{b.map((x, i) => <Badge key={i} variant="outline" className={`rounded-sm text-[9px] px-1 ${x.c}`}>{x.t}</Badge>)}</div> : <span className="text-muted-foreground text-[10px]">—</span>;
};

const KpiCard = ({ label, value, sub, tone, testid }) => (
  <div className={`border rounded-sm p-2 ${tone || "border-border"}`} data-testid={testid}>
    <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold leading-tight">{value}</div>
    {sub && <div className="text-[9px] text-muted-foreground">{sub}</div>}
  </div>
);

const VendorCard = ({ name, d, color }) => (
  <div className="border border-border rounded-sm p-3" data-testid={`also-vendor-${name.toLowerCase()}`}>
    <div className={`text-xs font-semibold mb-1 ${color}`}>{name}</div>
    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
      <span className="text-muted-foreground">Clienti</span><span className="text-right">{d.clients}</span>
      <span className="text-muted-foreground">Subscription</span><span className="text-right">{d.subscriptions}</span>
      <span className="text-muted-foreground">Seat</span><span className="text-right">{d.seats}</span>
      <span className="text-muted-foreground">Fatturato</span><span className="text-right">{eur(d.revenue)}</span>
      <span className="text-muted-foreground">Costo</span><span className="text-right">{eur(d.cost)}</span>
      <span className="text-muted-foreground">Margine</span><span className="text-right font-medium">{eur(d.margin)}</span>
      <span className="text-muted-foreground">Margine %</span><span className="text-right">{pct(d.margin_pct)}</span>
      <span className="text-muted-foreground">Scad. 30/60/90</span><span className="text-right">{d.expiring_30}/{d.expiring_60}/{d.expiring_90}</span>
    </div>
  </div>
);

function LicensesTab() {
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  const [filters, setFilters] = useState({ vendor: "", mapping: "", q: "", expiring: "", pp_expiring: "", renew_terminate: "", only_missing_price: false, only_missing_qty: false, only_negative_margin: false });
  const [includeTech, setIncludeTech] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sum, setSum] = useState(null);

  const qs = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v === true) p.append(k, "true"); else if (v) p.append(k, v); });
    if (includeTech) p.append("include_technical", "1");
    return p.toString();
  }, [filters, includeTech]);

  const loadSummary = useCallback(() => {
    api.get("/integrations/also/summary").then(({ data }) => setSum(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const q = qs();
    api.get(`/integrations/also/licenses?${q}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
    api.get(`/integrations/also/licenses/dashboard?${q}`).then(({ data }) => setDash(data)).catch(() => setDash(null));
  }, [qs]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/also/licenses/import", { include_technical: includeTech, require_mapping: true });
      if (data.ok) toast.success("Import licenze", { description: data.message });
      else toast.error("Import non riuscito", { description: data.message });
      load(); loadSummary();
    } catch (e) { toast.error(errMsg(e, "Errore import licenze.")); }
    finally { setBusy(false); }
  };

  const reclassify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/also/licenses/reclassify-vendors", {});
      if (data.ok) toast.success("Vendor ricalcolati", { description: data.message });
      load();
    } catch (e) { toast.error(errMsg(e, "Errore ricalcolo vendor.")); }
    finally { setBusy(false); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/integrations/also/licenses/export?${qs()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "licenze_cloud.xlsx"; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export Excel generato");
    } catch (e) { toast.error(errMsg(e, "Errore export Excel.")); }
  };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/integrations/also/licenses/${id}`); setDetail(data); }
    catch (e) { toast.error(errMsg(e, "Errore dettaglio.")); }
  };

  const k = dash?.kpi;
  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div data-testid="also-licenses-tab">
      <div className="rounded-sm bg-blue-50 border border-blue-200 text-blue-800 text-[11px] p-2.5 mb-3">Sezione separata dai 268 servizi CSP storici. Solo licenze vendute; tenant/tecnici esclusi dai totali. I record senza quantità non sono conteggiati.</div>
      {sum && (sum.licenses_pending_mapping > 0 || sum.licenses_ready_to_import > 0) && (
        <div className={`rounded-sm text-[11px] p-2.5 mb-3 flex items-start gap-2 border ${sum.licenses_pending_mapping > 0 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-green-50 border-green-200 text-green-800"}`} data-testid="also-lic-guidance">
          <Warning size={14} className="mt-0.5 shrink-0" />
          <span>
            In staging: <b>{sum.licenses_ready_to_import}</b> licenze pronte (mapping confermato)
            {sum.licenses_pending_mapping > 0 && <> e <b>{sum.licenses_pending_mapping}</b> in attesa di mapping cliente confermato — vai su "Clienti ALSO", associa e conferma il cliente TecSuite prima di importare</>}.
            {sum.licenses_imported > 0 && <> Già importate: <b>{sum.licenses_imported}</b>.</>}
          </span>
        </div>
      )}

      {k && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4" data-testid="also-lic-kpi">
          <KpiCard testid="kpi-clients" label="Clienti" value={k.clients} />
          <KpiCard testid="kpi-subs" label="Subscription" value={k.subscriptions} sub={`${k.seats} seat`} />
          <KpiCard testid="kpi-revenue" label="Fatturato/mese" value={eur(k.revenue)} />
          <KpiCard testid="kpi-cost" label="Costo/mese" value={eur(k.cost)} />
          <KpiCard testid="kpi-margin" label="Margine/mese" value={eur(k.margin)} tone={k.margin < 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"} />
          <KpiCard testid="kpi-marginpct" label="Margine % medio" value={pct(k.margin_pct)} />
          <KpiCard testid="kpi-exp" label="Scad. 30/60/90" value={`${k.expiring_30}/${k.expiring_60}/${k.expiring_90}`} tone={k.expiring_30 ? "border-amber-300 bg-amber-50" : ""} />
          <KpiCard testid="kpi-pp" label="Price Prot. 30/60/90" value={`${k.pp_30}/${k.pp_60}/${k.pp_90}`} tone={k.pp_30 ? "border-purple-300 bg-purple-50" : ""} />
          <KpiCard testid="kpi-terminate" label="Terminate" value={k.terminate} tone={k.terminate ? "border-red-300 bg-red-50" : ""} />
          <KpiCard testid="kpi-renew" label="Renew" value={k.renew} />
          <KpiCard testid="kpi-missprice" label="Prezzo mancante" value={k.missing_price} tone={k.missing_price ? "border-yellow-300 bg-yellow-50" : ""} />
          <KpiCard testid="kpi-missqty" label="Q.tà mancante" value={k.missing_qty} tone={k.missing_qty ? "border-yellow-300 bg-yellow-50" : ""} />
        </div>
      )}

      {dash && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
          <VendorCard name="Microsoft" d={dash.by_vendor.microsoft} color="text-blue-700" />
          <VendorCard name="Adobe" d={dash.by_vendor.adobe} color="text-red-700" />
          <VendorCard name="Other" d={dash.by_vendor.other} color="text-zinc-600" />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <Button size="sm" className="rounded-sm gap-2" disabled={busy} onClick={doImport} data-testid="also-lic-import-btn"><ArrowsClockwise size={14} /> {busy ? "…" : "Importa/aggiorna da staging"}</Button>
        <Button size="sm" variant="outline" className="rounded-sm gap-2" onClick={exportExcel} data-testid="also-lic-export-btn"><DownloadSimple size={14} /> Esporta Excel</Button>
        <Button size="sm" variant="outline" className="rounded-sm gap-2" disabled={busy} onClick={reclassify} data-testid="also-lic-reclassify-btn"><ChartBar size={14} /> Ricalcola vendor</Button>
        <label className="flex items-center gap-1.5 text-[11px]"><Checkbox checked={includeTech} onCheckedChange={(v) => setIncludeTech(!!v)} data-testid="also-lic-tech" /> Mostra tecnici/tenant</label>
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div><Label className="text-[10px] uppercase text-muted-foreground">Cerca</Label><Input value={filters.q} onChange={(e) => setF({ q: e.target.value })} placeholder="prodotto, cliente…" className="h-8 rounded-sm text-xs w-44" data-testid="also-lic-q" /></div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Vendor</Label>
          <Select value={filters.vendor || "all"} onValueChange={(v) => setF({ vendor: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-28" data-testid="also-lic-vendor"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="microsoft">Microsoft</SelectItem><SelectItem value="adobe">Adobe</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Mapping</Label>
          <Select value={filters.mapping || "all"} onValueChange={(v) => setF({ mapping: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-28" data-testid="also-lic-mapping"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="yes">Associati</SelectItem><SelectItem value="no">Non associati</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Scadenza</Label>
          <Select value={filters.expiring || "all"} onValueChange={(v) => setF({ expiring: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-28" data-testid="also-lic-expiring"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="30">≤ 30 giorni</SelectItem><SelectItem value="60">≤ 60 giorni</SelectItem><SelectItem value="90">≤ 90 giorni</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Price Prot.</Label>
          <Select value={filters.pp_expiring || "all"} onValueChange={(v) => setF({ pp_expiring: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-28" data-testid="also-lic-pp"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="30">≤ 30 giorni</SelectItem><SelectItem value="60">≤ 60 giorni</SelectItem><SelectItem value="90">≤ 90 giorni</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Fine periodo</Label>
          <Select value={filters.renew_terminate || "all"} onValueChange={(v) => setF({ renew_terminate: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 rounded-sm text-xs w-28" data-testid="also-lic-rt"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="renew">Renew</SelectItem><SelectItem value="terminate">Terminate</SelectItem></SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-1.5 text-[11px]"><Checkbox checked={filters.only_missing_price} onCheckedChange={(v) => setF({ only_missing_price: !!v })} data-testid="also-lic-f-missprice" /> Prezzo mancante</label>
        <label className="flex items-center gap-1.5 text-[11px]"><Checkbox checked={filters.only_missing_qty} onCheckedChange={(v) => setF({ only_missing_qty: !!v })} data-testid="also-lic-f-missqty" /> Q.tà mancante</label>
        <label className="flex items-center gap-1.5 text-[11px]"><Checkbox checked={filters.only_negative_margin} onCheckedChange={(v) => setF({ only_negative_margin: !!v })} data-testid="also-lic-f-negmargin" /> Margine negativo</label>
      </div>

      {dash && dash.by_client.length > 0 && (
        <div className="mb-4" data-testid="also-lic-byclient">
          <p className="text-xs font-medium mb-1">Margini per cliente</p>
          <div className="overflow-x-auto border border-border/60 rounded-sm">
            <table className="w-full text-[11px]">
              <thead className="text-muted-foreground border-b border-border/60"><tr className="text-left"><th className="py-1 px-2">Cliente TecSuite</th><th className="py-1 px-2">Cliente ALSO</th><th className="py-1 px-2">Vendor</th><th className="py-1 px-2">Subs</th><th className="py-1 px-2">Seat</th><th className="py-1 px-2">Fatturato</th><th className="py-1 px-2">Costo</th><th className="py-1 px-2">Margine</th><th className="py-1 px-2">Marg.%</th><th className="py-1 px-2">Scad.</th><th className="py-1 px-2">Term.</th><th className="py-1 px-2">Ultimo</th></tr></thead>
              <tbody>
                {dash.by_client.map((c) => (
                  <tr key={c.client_key} className="border-b border-border/30" data-testid="also-lic-client-row">
                    <td className="py-1 px-2">{c.client_name}</td>
                    <td className="py-1 px-2 text-muted-foreground">{c.also_name || "—"}</td>
                    <td className="py-1 px-2">{c.vendors.join(", ") || "—"}</td>
                    <td className="py-1 px-2">{c.subscriptions}</td>
                    <td className="py-1 px-2">{c.seats}</td>
                    <td className="py-1 px-2">{eur(c.revenue)}</td>
                    <td className="py-1 px-2">{eur(c.cost)}</td>
                    <td className={`py-1 px-2 font-medium ${c.margin < 0 ? "text-red-700" : ""}`}>{eur(c.margin)}</td>
                    <td className="py-1 px-2">{pct(c.margin_pct)}</td>
                    <td className="py-1 px-2">{c.expiring_90 ? `${c.expiring_30}/${c.expiring_60}/${c.expiring_90}` : "—"}</td>
                    <td className="py-1 px-2">{c.terminate || "—"}</td>
                    <td className="py-1 px-2 text-muted-foreground">{c.last_sync ? String(c.last_sync).slice(0, 10) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length === 0
        ? <p className="text-xs text-muted-foreground py-6 text-center">Nessuna licenza importata (o nessuna corrisponde ai filtri). Leggi le subscription (scheda Licenze), conferma i mapping, poi "Importa/aggiorna da staging".</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="also-lic-table">
              <thead className="text-muted-foreground border-b border-border/60"><tr className="text-left"><th className="py-1 pr-2">Cliente TecSuite</th><th className="py-1 pr-2">Cliente ALSO</th><th className="py-1 pr-2">Vendor</th><th className="py-1 pr-2">Prodotto</th><th className="py-1 pr-2">Q.tà</th><th className="py-1 pr-2">Stato</th><th className="py-1 pr-2">Avvisi</th><th className="py-1 pr-2">Acq.</th><th className="py-1 pr-2">Vend.</th><th className="py-1 pr-2">Marg./mese</th><th className="py-1"></th></tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className="border-b border-border/40" data-testid="also-lic-row">
                    <td className="py-1.5 pr-2">{s.tecsuite_client_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="py-1.5 pr-2">{s.external_account_name || s.customer_account_id || "—"}</td>
                    <td className="py-1.5 pr-2"><Badge variant="outline" className={`rounded-sm text-[10px] ${s.vendor === "microsoft" ? "bg-blue-100 text-blue-800" : s.vendor === "adobe" ? "bg-red-100 text-red-800" : "bg-zinc-100 text-zinc-600"}`}>{s.vendor}</Badge></td>
                    <td className="py-1.5 pr-2">{s.product_name || s.service_display_name || "—"}</td>
                    <td className="py-1.5 pr-2">{s.quantity ?? "—"}</td>
                    <td className="py-1.5 pr-2">{s.subscription_status || s.status || "—"}</td>
                    <td className="py-1.5 pr-2"><LicBadges s={s} /></td>
                    <td className="py-1.5 pr-2">{eur(s.purchase_price)}</td>
                    <td className="py-1.5 pr-2">{eur(s.sales_price)}</td>
                    <td className={`py-1.5 pr-2 ${(s.margine_totale_mensile ?? 0) < 0 ? "text-red-700" : ""}`}>{eur(s.margine_totale_mensile)}</td>
                    <td className="py-1.5"><Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => openDetail(s.id)} data-testid="also-lic-detail-btn">Dettaglio</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="also-lic-detail">
          <DialogHeader><DialogTitle>{detail?.product_name || detail?.service_display_name}</DialogTitle><DialogDescription>{detail?.external_account_name} · Tenant {detail?.microsoft_tenant_id || "—"} · OCP {detail?.ocp_subscription_id || "—"} · Order {detail?.order_id || "—"}</DialogDescription></DialogHeader>
          {detail && (
            <div className="text-[11px] space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {[["Q.tà", detail.quantity], ["Acquisto", eur(detail.purchase_price)], ["Vendita", eur(detail.sales_price)], ["Listino", eur(detail.suggested_retail_price)], ["Margine unit.", eur(detail.margine_unitario)], ["Margine/mese", eur(detail.margine_totale_mensile)], ["Provisioning", detail.provisioning_status], ["Billing", detail.billing_type], ["Fine periodo", detail.advance_period_end_action]].map(([k, v]) => (
                  <div key={k} className="border border-border/60 rounded-sm p-1.5"><div className="text-muted-foreground text-[10px]">{k}</div><div>{v ?? "—"}</div></div>
                ))}
              </div>
              {detail.fields_list?.length > 0 && (<div><div className="font-medium mb-1">Fields</div><table className="w-full"><tbody>{detail.fields_list.map((f, i) => (<tr key={i} className="border-b border-border/30"><td className="pr-2 text-muted-foreground">{f.display_name || f.name}</td><td className="font-mono">{String(f.value ?? "")}</td></tr>))}</tbody></table></div>)}
              <details><summary className="cursor-pointer text-muted-foreground">Raw JSON sanitizzato</summary><JsonPreview data={detail.raw_data || {}} /></details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
